import React, { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'athenian_progress_float_pos';
const EXPANDED_POS_KEY = 'athenian_progress_float_expanded_pos';
const DOT_POS_KEY = 'athenian_progress_dot_pos';
const MINIMIZED_KEY = 'athenian_progress_float_minimized';
const CARD_W = 340;
const CARD_H_EST = 168;
const DOT_SIZE = 44;
const MARGIN = 12;
const LONG_PRESS_MS = 480;
const LONG_PRESS_MOVE_CANCEL_PX = 18;
const TAP_MAX_MOVE_PX = 8;
const HEADER_OFFSET = 64;

function isTouchLikePointer(pointerType) {
  return pointerType === 'touch' || pointerType === 'pen';
}

function loadSavedPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Number.isFinite(parsed?.x) && Number.isFinite(parsed?.y)) return parsed;
  } catch { /* ignore */ }
  return null;
}

function loadExpandedPosition() {
  try {
    const raw = localStorage.getItem(EXPANDED_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Number.isFinite(parsed?.x) && Number.isFinite(parsed?.y)) return parsed;
  } catch { /* ignore */ }
  return null;
}

function loadMinimized() {
  try {
    return localStorage.getItem(MINIMIZED_KEY) === 'true';
  } catch { /* ignore */ }
  return false;
}

function loadDotPosition() {
  try {
    const raw = localStorage.getItem(DOT_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Number.isFinite(parsed?.x) && Number.isFinite(parsed?.y)) return parsed;
  } catch { /* ignore */ }
  return null;
}

function defaultDotPosition() {
  if (typeof window === 'undefined') return { x: MARGIN, y: HEADER_OFFSET + MARGIN };
  return {
    x: Math.max(MARGIN, window.innerWidth - DOT_SIZE - MARGIN),
    y: Math.max(HEADER_OFFSET + MARGIN, window.innerHeight - DOT_SIZE - MARGIN),
  };
}

function fitDotPosition(pos) {
  return fitPositionToViewport(pos || defaultDotPosition(), DOT_SIZE, DOT_SIZE);
}

function getCardWidth() {
  if (typeof window === 'undefined') return CARD_W;
  return Math.min(CARD_W, window.innerWidth - MARGIN * 2);
}

function defaultPosition() {
  if (typeof window === 'undefined') return { x: MARGIN, y: MARGIN };
  const w = getCardWidth();
  return {
    x: Math.max(MARGIN, window.innerWidth - w - MARGIN),
    y: Math.max(HEADER_OFFSET + MARGIN, window.innerHeight - CARD_H_EST - MARGIN),
  };
}

function fitPositionToViewport(pos, cardW, cardH) {
  const base = pos || defaultPosition();
  return clampPosition(base.x, base.y, cardW, cardH);
}

function clampPosition(x, y, cardW, cardH) {
  const maxX = Math.max(MARGIN, window.innerWidth - cardW - MARGIN);
  const maxY = Math.max(HEADER_OFFSET + MARGIN, window.innerHeight - cardH - MARGIN);
  return {
    x: Math.min(maxX, Math.max(MARGIN, x)),
    y: Math.min(maxY, Math.max(HEADER_OFFSET + MARGIN, y)),
  };
}

function TimeDisplay({ seconds, blink = true, suffix = ' left' }) {
  if (seconds == null || Number.isNaN(seconds)) {
    return <span className="tabular-nums">—</span>;
  }
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  const blinkCls = blink ? 'processing-time-blink' : '';

  if (total < 60) {
    return (
      <span className="tabular-nums">
        <span className={blinkCls}>{total}</span>
        <span className="text-gray-400 dark:text-slate-500 font-normal">s{suffix}</span>
      </span>
    );
  }
  if (h > 0) {
    return (
      <span className="tabular-nums">
        {h}h {m}m{' '}
        <span className={blinkCls}>{ss}s</span>
        <span className="text-gray-400 dark:text-slate-500 font-normal">{suffix}</span>
      </span>
    );
  }
  return (
    <span className="tabular-nums">
      {m}m{' '}
      <span className={blinkCls}>{ss}s</span>
      <span className="text-gray-400 dark:text-slate-500 font-normal">{suffix}</span>
    </span>
  );
}

/**
 * Single draggable floating progress card — desktop, tablet, and mobile.
 */
export default function ProcessingProgressFloat({
  visible,
  percent = 0,
  remainingSeconds,
  estimatedSeconds,
  phaseLabel = 'Processing…',
  queued = false,
  queuePosition = null,
  started = false,
  awaitingResults = false,
  resumed = false,
  studentName = null,
  cycleLabel = null,
  cycleCount = 0,
  cycleIndex = null,
}) {
  const cardRef = useRef(null);
  const dragRef = useRef({
    active: false,
    pendingLongPress: false,
    pendingMouseDrag: false,
    pointerId: null,
    ox: 0,
    oy: 0,
    pressX: 0,
    pressY: 0,
    lastX: 0,
    lastY: 0,
    longPressTimer: null,
  });
  const [minimized, setMinimized] = useState(loadMinimized);
  const [expandedPosition, setExpandedPosition] = useState(loadExpandedPosition);
  const [position, setPosition] = useState(() => {
    if (loadMinimized()) {
      return fitDotPosition(loadDotPosition());
    }
    const saved = loadSavedPosition();
    if (saved) {
      return fitPositionToViewport(saved, getCardWidth(), CARD_H_EST);
    }
    return null;
  });
  const [entered, setEntered] = useState(false);
  const [flash, setFlash] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [longPressPending, setLongPressPending] = useState(false);
  const prevLabelRef = useRef(phaseLabel);

  const pct = Math.max(0, Math.min(100, percent || 0));
  const barPct = Math.max(pct, queued ? 8 : started ? 4 : 3);

  const persistPosition = useCallback((pos) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch { /* ignore */ }
  }, []);

  const persistMinimized = useCallback((value) => {
    try {
      localStorage.setItem(MINIMIZED_KEY, value ? 'true' : 'false');
    } catch { /* ignore */ }
  }, []);

  const persistExpandedPosition = useCallback((pos) => {
    try {
      localStorage.setItem(EXPANDED_POS_KEY, JSON.stringify(pos));
    } catch { /* ignore */ }
  }, []);

  const persistDotPosition = useCallback((pos) => {
    try {
      localStorage.setItem(DOT_POS_KEY, JSON.stringify(pos));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!visible) {
      setEntered(false);
      setMinimized(false);
      persistMinimized(false);
      return undefined;
    }
    const t = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(t);
  }, [persistMinimized, visible]);

  useEffect(() => {
    if (!visible || !phaseLabel || phaseLabel === prevLabelRef.current) return;
    prevLabelRef.current = phaseLabel;
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 500);
    return () => clearTimeout(t);
  }, [phaseLabel, visible]);

  useEffect(() => {
    if (position) return;
    setPosition(defaultPosition());
  }, [position]);

  useEffect(() => {
    if (!visible || minimized) return undefined;
    const fitToScreen = () => {
      const el = cardRef.current;
      const cardW = el ? el.getBoundingClientRect().width : getCardWidth();
      const cardH = el ? el.getBoundingClientRect().height : CARD_H_EST;
      setPosition((prev) => {
        const fitted = fitPositionToViewport(prev, cardW, cardH);
        if (fitted.x !== prev?.x || fitted.y !== prev?.y) {
          persistPosition(fitted);
          setExpandedPosition(fitted);
          persistExpandedPosition(fitted);
        }
        return fitted;
      });
    };
    const raf = requestAnimationFrame(fitToScreen);
    return () => cancelAnimationFrame(raf);
  }, [minimized, persistExpandedPosition, persistPosition, visible]);

  useEffect(() => {
    if (!visible || !minimized) return undefined;
    const fitDotToScreen = () => {
      setPosition((prev) => {
        const fitted = fitDotPosition(prev);
        persistDotPosition(fitted);
        return fitted;
      });
    };
    const raf = requestAnimationFrame(fitDotToScreen);
    return () => cancelAnimationFrame(raf);
  }, [minimized, persistDotPosition, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const onResize = () => {
      if (!cardRef.current) return;
      if (minimized) {
        setPosition((prev) => {
          const fitted = fitDotPosition(prev);
          persistDotPosition(fitted);
          return fitted;
        });
        return;
      }
      const { width, height } = cardRef.current.getBoundingClientRect();
      setPosition((prev) => {
        const base = prev || defaultPosition();
        return fitPositionToViewport(base, width, height);
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [minimized, persistDotPosition, visible]);

  const handleMinimize = useCallback(() => {
    setPosition((currentPos) => {
      const cardW = getCardWidth();
      const expanded = fitPositionToViewport(
        currentPos || expandedPosition || defaultPosition(),
        cardW,
        CARD_H_EST,
      );
      setExpandedPosition(expanded);
      persistExpandedPosition(expanded);
      const dotPos = fitDotPosition(loadDotPosition());
      persistDotPosition(dotPos);
      setMinimized(true);
      persistMinimized(true);
      return dotPos;
    });
  }, [expandedPosition, persistDotPosition, persistExpandedPosition, persistMinimized]);

  const handleExpand = useCallback(() => {
    const raw = expandedPosition || loadExpandedPosition() || defaultPosition();
    const cardW = getCardWidth();
    const next = fitPositionToViewport(raw, cardW, CARD_H_EST);
    setPosition(next);
    persistPosition(next);
    setExpandedPosition(next);
    persistExpandedPosition(next);
    setMinimized(false);
    persistMinimized(false);
    setEntered(false);
    requestAnimationFrame(() => setEntered(true));
  }, [expandedPosition, persistExpandedPosition, persistMinimized, persistPosition]);

  const clearLongPress = useCallback(() => {
    const d = dragRef.current;
    if (d.longPressTimer) {
      clearTimeout(d.longPressTimer);
      d.longPressTimer = null;
    }
    d.pendingLongPress = false;
    setLongPressPending(false);
  }, []);

  const clearPendingMouseDrag = useCallback(() => {
    dragRef.current.pendingMouseDrag = false;
  }, []);

  const releasePointer = useCallback((pointerId) => {
    if (pointerId == null) return;
    try {
      cardRef.current?.releasePointerCapture(pointerId);
    } catch { /* ignore */ }
  }, []);

  const endDrag = useCallback((e) => {
    const d = dragRef.current;
    if (!d.active || d.pointerId !== e.pointerId) return;
    d.active = false;
    d.pointerId = null;
    setDragging(false);
    releasePointer(e.pointerId);
    setPosition((prev) => {
      if (prev) {
        persistPosition(prev);
        if (minimized) {
          persistDotPosition(prev);
        } else {
          setExpandedPosition(prev);
          persistExpandedPosition(prev);
        }
      }
      return prev;
    });
  }, [minimized, persistDotPosition, persistExpandedPosition, persistPosition, releasePointer]);

  const updateDragPosition = useCallback((clientX, clientY) => {
    const el = cardRef.current;
    const d = dragRef.current;
    if (!el || !d.active) return;
    const { width, height } = el.getBoundingClientRect();
    setPosition(clampPosition(
      clientX - d.ox,
      clientY - d.oy,
      width,
      height,
    ));
  }, []);

  const startDrag = useCallback((pointerId, clientX, clientY) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const d = dragRef.current;
    d.active = true;
    d.pendingLongPress = false;
    d.pointerId = pointerId;
    d.ox = clientX - rect.left;
    d.oy = clientY - rect.top;
    d.lastX = clientX;
    d.lastY = clientY;
    setLongPressPending(false);
    setDragging(true);
    try {
      el.setPointerCapture(pointerId);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const onWindowPointerMove = (e) => {
      const d = dragRef.current;

      if (d.pointerId === e.pointerId) {
        d.lastX = e.clientX;
        d.lastY = e.clientY;
      }

      if (d.pendingMouseDrag && d.pointerId === e.pointerId && minimized) {
        const moved = Math.hypot(e.clientX - d.pressX, e.clientY - d.pressY);
        if (moved > TAP_MAX_MOVE_PX) {
          d.pendingMouseDrag = false;
          startDrag(e.pointerId, e.clientX, e.clientY);
        }
        return;
      }

      if (d.pendingLongPress && d.pointerId === e.pointerId) {
        if (isTouchLikePointer(e.pointerType)) {
          e.preventDefault();
        }
        const moved = Math.hypot(e.clientX - d.pressX, e.clientY - d.pressY);
        if (moved > LONG_PRESS_MOVE_CANCEL_PX) {
          clearLongPress();
          releasePointer(e.pointerId);
        }
        return;
      }

      if (!d.active || d.pointerId !== e.pointerId) return;
      if (isTouchLikePointer(e.pointerType)) {
        e.preventDefault();
      }
      updateDragPosition(e.clientX, e.clientY);
    };

    const onWindowPointerUp = (e) => {
      const d = dragRef.current;
      if (d.pendingLongPress && d.pointerId === e.pointerId) {
        clearLongPress();
        releasePointer(e.pointerId);
        const moved = Math.hypot(e.clientX - d.pressX, e.clientY - d.pressY);
        if (minimized && moved <= TAP_MAX_MOVE_PX) {
          handleExpand();
        }
        d.pointerId = null;
        return;
      }
      if (d.pendingMouseDrag && d.pointerId === e.pointerId) {
        clearPendingMouseDrag();
        releasePointer(e.pointerId);
        const moved = Math.hypot(e.clientX - d.pressX, e.clientY - d.pressY);
        if (minimized && moved <= TAP_MAX_MOVE_PX) {
          handleExpand();
        }
        d.pointerId = null;
        return;
      }
      if (d.active) {
        endDrag(e);
        return;
      }
      if (d.pointerId === e.pointerId) {
        d.pointerId = null;
      }
    };

    window.addEventListener('pointermove', onWindowPointerMove, { passive: false });
    window.addEventListener('pointerup', onWindowPointerUp);
    window.addEventListener('pointercancel', onWindowPointerUp);
    return () => {
      window.removeEventListener('pointermove', onWindowPointerMove);
      window.removeEventListener('pointerup', onWindowPointerUp);
      window.removeEventListener('pointercancel', onWindowPointerUp);
      clearLongPress();
      clearPendingMouseDrag();
    };
  }, [clearLongPress, clearPendingMouseDrag, endDrag, handleExpand, minimized, releasePointer, startDrag, updateDragPosition]);

  const onDragHandlePointerDown = useCallback((e) => {
    if (e.button !== 0 && e.pointerType === 'mouse') return;

    const el = cardRef.current;
    if (!el) return;

    const d = dragRef.current;
    d.pointerId = e.pointerId;
    d.pressX = e.clientX;
    d.pressY = e.clientY;
    d.lastX = e.clientX;
    d.lastY = e.clientY;

    if (isTouchLikePointer(e.pointerType)) {
      d.pendingLongPress = true;
      setLongPressPending(true);
      const pointerId = e.pointerId;
      try {
        el.setPointerCapture(pointerId);
      } catch { /* ignore */ }
      d.longPressTimer = setTimeout(() => {
        if (!d.pendingLongPress || d.pointerId !== pointerId) return;
        startDrag(pointerId, d.lastX, d.lastY);
      }, LONG_PRESS_MS);
      e.preventDefault();
      return;
    }

    if (minimized) {
      d.pendingMouseDrag = true;
      e.preventDefault();
      return;
    }

    startDrag(e.pointerId, e.clientX, e.clientY);
    e.preventDefault();
  }, [minimized, startDrag]);

  if (!visible || !position) return null;

  const headline = studentName
    ? `Compliance — ${studentName}`
    : 'Compliance processing';
  const showCycle = cycleCount > 1 && Boolean(cycleLabel);
  const resolvedIndex = Number.isFinite(cycleIndex) && cycleIndex > 0 ? cycleIndex : (cycleCount > 1 ? 1 : null);
  const hasCycleIndex = Number.isFinite(resolvedIndex) && resolvedIndex > 0;
  const cycleBadgeText = hasCycleIndex
    ? `${cycleLabel} · ${resolvedIndex}/${cycleCount}`
    : `${cycleLabel} · ${cycleCount} cycles`;
  const cycleBadgeTitle = hasCycleIndex
    ? `Processing ${cycleLabel} — cycle ${resolvedIndex} of ${cycleCount}`
    : `Showing ${cycleLabel} — ${cycleCount} IEP cycles for this student`;

  let timeNode;
  let footerHint;
  if (queued) {
    timeNode = (
      <span className="tabular-nums text-[11px] sm:text-xs font-semibold text-amber-600 dark:text-amber-400">
        {queuePosition ? `Queue #${queuePosition}` : 'In queue…'}
      </span>
    );
    footerHint = queuePosition
      ? `Waiting for a free worker slot (max 2 at a time) — position ${queuePosition}. Timer starts when processing begins.`
      : 'Waiting for a free worker slot (max 2 jobs at a time) — timer starts when processing begins.';
  } else if (!started) {
    timeNode = (
      <TimeDisplay
        seconds={estimatedSeconds ?? remainingSeconds}
        blink
        suffix=" est."
      />
    );
    footerHint = 'Estimate is based on page count; countdown starts when the job runs.';
  } else if (awaitingResults) {
    timeNode = <TimeDisplay seconds={remainingSeconds ?? 30} blink />;
    footerHint = 'Saving results — the report refreshes when data is stored.';
  } else {
    timeNode = <TimeDisplay seconds={remainingSeconds} blink />;
    footerHint = 'Progress updates as each page and section completes.';
  }

  const cardWidth = getCardWidth();

  const dragHandleCls = dragging
    ? 'cursor-grabbing'
    : longPressPending
      ? 'cursor-wait ring-2 ring-indigo-400/60 ring-inset'
      : 'cursor-grab';

  const touchDragCls = dragging || longPressPending ? 'touch-none' : '';

  if (minimized) {
    return (
      <div
        ref={cardRef}
        className={`fixed z-[90] select-none transition-[opacity,transform] duration-300 ease-out ${
          entered ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
        } ${touchDragCls}`}
        style={{
          left: position.x,
          top: position.y,
          width: DOT_SIZE,
          height: DOT_SIZE,
        }}
        role="status"
        aria-live="polite"
        aria-label={`Compliance processing ${Math.round(pct)} percent — tap to expand, drag to move`}
      >
        <div
          className={`relative flex h-full w-full items-center justify-center rounded-full border-2 border-indigo-500/80 bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 dark:shadow-indigo-900/50 ${dragHandleCls}`}
          style={{ touchAction: 'none' }}
          onPointerDown={onDragHandlePointerDown}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleExpand();
            }
          }}
          aria-label="Compliance progress minimized — tap to expand, long-press and drag to move"
        >
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-indigo-400 opacity-30 pointer-events-none" />
          <span className="relative text-[11px] font-bold tabular-nums leading-none pointer-events-none">
            {Math.round(pct)}
            <span className="text-[8px] font-semibold opacity-80">%</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className={`fixed z-[90] select-none transition-[opacity,transform] duration-500 ease-out ${
        entered ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      } ${flash ? 'processing-card-flash' : ''} ${touchDragCls}`}
      style={{
        left: position.x,
        top: position.y,
        width: cardWidth,
        maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
      }}
      role="status"
      aria-live="polite"
      aria-label={`Processing ${Math.round(pct)} percent complete`}
    >
      <div className="rounded-2xl border border-gray-200/90 dark:border-slate-600/80 bg-white/97 dark:bg-slate-900/97 backdrop-blur-md shadow-2xl shadow-indigo-500/15 dark:shadow-black/50 overflow-hidden">
        {/* Drag handle */}
        <div
          className={`flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-indigo-50/90 via-white to-violet-50/90 dark:from-slate-800/90 dark:via-slate-900 dark:to-indigo-950/50 border-b border-gray-100/80 dark:border-slate-700/60 ${dragHandleCls}`}
          style={{ touchAction: 'none' }}
          onPointerDown={onDragHandlePointerDown}
          aria-label="Drag handle — click and drag on desktop, long-press and drag on touch devices"
        >
          <div className="flex flex-col gap-[3px] shrink-0 opacity-40" aria-hidden>
            <span className="block w-4 h-0.5 rounded-full bg-gray-500 dark:bg-slate-400" />
            <span className="block w-4 h-0.5 rounded-full bg-gray-500 dark:bg-slate-400" />
          </div>
          <div className="relative shrink-0 flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-indigo-400 opacity-25" />
            <svg className="relative w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 truncate">
                {headline}
              </p>
              {resumed && (
                <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-200 shrink-0">
                  Resumed
                </span>
              )}
              {showCycle && (
                <span
                  className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-200 shrink-0"
                  title={cycleBadgeTitle}
                >
                  {cycleBadgeText}
                </span>
              )}
            </div>
          </div>
          <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-white leading-none shrink-0">
            {Math.round(pct)}
            <span className="text-xs font-semibold text-gray-400 dark:text-slate-500">%</span>
          </p>
          <button
            type="button"
            className="shrink-0 flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-slate-700/60 transition-colors"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              handleMinimize();
            }}
            aria-label="Minimize progress panel"
            title="Minimize"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <div className="px-3 sm:px-4 pt-3 pb-2.5">
          <p className={`text-sm font-medium text-gray-900 dark:text-white leading-snug mb-2.5 line-clamp-2 transition-colors duration-300 ${
            flash ? 'text-indigo-700 dark:text-indigo-200' : ''
          }`}
          >
            {phaseLabel}
          </p>

          <div className="flex items-end justify-between gap-3 mb-2.5">
            <p className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-slate-400">
              {timeNode}
            </p>
          </div>

          <div className="h-2.5 rounded-full bg-gray-100 dark:bg-slate-800 overflow-hidden relative">
            <div
              className="processing-bar-fill h-full rounded-full relative"
              style={{ width: `${barPct}%` }}
            >
              <div className="processing-bar-shimmer absolute inset-0" />
            </div>
            {queued && (
              <div className="processing-bar-indeterminate absolute inset-y-0 w-1/3 rounded-full bg-indigo-400/40" />
            )}
          </div>
        </div>

        <div className="px-3 sm:px-4 py-2 bg-gray-50/90 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-700/70 flex items-start gap-2">
          <span className="relative flex h-2 w-2 mt-0.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
          </span>
          <p className="text-[10px] sm:text-[11px] leading-relaxed text-gray-500 dark:text-slate-400">
            {footerHint}
          </p>
        </div>
      </div>
    </div>
  );
}
